import React, { useState, useEffect } from 'react';
import { PieChart, Wallet, Users, Clock, Construction, TrendingUp, ChevronRight } from 'lucide-react';
import api from '../api/axiosInstance';
import { Link } from 'react-router-dom';

const SiteCostAnalysis = () => {
    const [report, setReport] = useState([]);
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [totalMonthlyLabor, setTotalMonthlyLabor] = useState(0);

    useEffect(() => { fetchAnalysis(); }, [month]);

    const fetchAnalysis = async () => {
        try {
            const res = await api.get(`/get_site_cost_analysis.php?month=${month}`);
            const data = res.data.data;
            setReport(data);
            const total = data.reduce((acc, curr) => acc + curr.total_labor_cost, 0);
            setTotalMonthlyLabor(total);
        } catch (err) { console.error(err); }
    };

    return (
        <div className="p-8 space-y-8 animate-in fade-in duration-700">
            {/* Header & Stats Summary */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-4xl font-black italic uppercase tracking-tighter text-slate-900">Project Labor Audit</h1>
                    <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest">Cost Attribution for {month}</p>
                </div>

                <div className="flex items-center gap-4 bg-white p-2 rounded-[32px] shadow-sm border border-slate-100">
                    <div className="px-6 py-3">
                        <p className="text-[8px] font-black text-slate-400 uppercase">Total Monthly Payout</p>
                        <p className="text-2xl font-black italic text-indigo-600">₹{totalMonthlyLabor.toLocaleString()}</p>
                    </div>
                    <input
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="bg-slate-900 text-white rounded-2xl px-6 py-4 font-black text-xs uppercase outline-none"
                    />
                </div>
            </div>

            {/* Analysis Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {report.map((site, index) => {
                    const percentage = totalMonthlyLabor > 0 ? ((site.total_labor_cost / totalMonthlyLabor) * 100).toFixed(1) : 0;

                    return (
                        <div key={index} className="group bg-white border border-slate-100 rounded-[48px] p-8 hover:shadow-2xl hover:border-indigo-500 transition-all duration-500 relative overflow-hidden">
                            {/* Percentage Indicator */}
                            <div className="absolute top-0 right-0 bg-slate-900 text-white px-6 py-2 rounded-bl-[24px] font-black text-[10px]">
                                {percentage}% OF TOTAL
                            </div>

                            <div className="mb-8">
                                <div className="bg-indigo-50 w-12 h-12 rounded-2xl flex items-center justify-center text-indigo-600 mb-4 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                    <Construction size={24} />
                                </div>
                                <h3 className="text-xl font-black uppercase italic leading-none text-slate-900">{site.project_name}</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mt-2">{site.client_name}</p>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <div className="flex justify-between items-end mb-2">
                                        <span className="text-[9px] font-black text-slate-400 uppercase">Budget Consumption</span>
                                        <span className="text-lg font-black italic text-slate-900">₹{site.total_labor_cost.toLocaleString()}</span>
                                    </div>
                                    {/* Progress Bar Visualizing % of Total Labor */}
                                    <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-indigo-600 rounded-full transition-all duration-1000"
                                            style={{ width: `${percentage}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-50 p-4 rounded-3xl">
                                        <div className="flex items-center gap-2 text-slate-400 mb-1">
                                            <Users size={12} />
                                            <span className="text-[8px] font-black uppercase">Team Size</span>
                                        </div>
                                        <p className="font-black text-slate-900">{site.unique_workers} Workers</p>
                                    </div>
                                    <div className="bg-slate-50 p-4 rounded-3xl">
                                        <div className="flex items-center gap-2 text-slate-400 mb-1">
                                            <Clock size={12} />
                                            <span className="text-[8px] font-black uppercase">Workload</span>
                                        </div>
                                        <p className="font-black text-slate-900 text-sm">
                                            {site.total_hours > 0 ? `${site.total_hours.toFixed(1)}H` : `${site.man_days}D`}
                                        </p>
                                    </div>
                                </div>

                                <Link
                                    to={`/site-audit/${site.project_id}`}
                                    className="w-full py-4 border-2 border-slate-900 rounded-2xl font-black text-[10px] uppercase flex items-center justify-center gap-2 group-hover:bg-slate-900 group-hover:text-white transition-all no-underline text-slate-900"
                                >
                                    View Full Site Audit <ChevronRight size={14} />
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SiteCostAnalysis;
